import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EnhancedDropDownRHFWithLabel,
	EnhancedSnackbar,
	StandardPanel,
} from "@websential/cosmic-v20";
import { FormProvider, useForm } from "react-hook-form";
import { Grid, useTheme } from "@mui/material";
import { IGetListRequest, IResponse, IServerError } from "../../../../models/common";
import { Link, useNavigate, useParams } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	getAddOnsScreenParentName,
	scrollPanelFieldsToTop,
	sortDDLByLabel,
	useNavigateToLogout,
} from "../../../../common/Common";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AccountSettingsService } from "../../../../services/AddOns/Accounting/AccountSettingsService";
import { AccountSettingsValidationSchema } from "./validator";
import { BasicSetupFormToolbar } from "../../../BasicSetupFormToolbar";
import { DashboardServices } from "../../../../services/common/DashboardServices";
import { EmployeeAdvanceInfoFieldsPanel } from "./Panels/EmployeeAdvanceInfoFieldsPanel";
import { ExpenseInfoFieldsPanel } from "./Panels/ExpenseInfoFieldsPanel";
import { IAppState } from "../../../../store/reducers";
import {
	INITIAL_SNACKBAR_DATA,
} from "../../../../common/constants";
import { LiabilitiesInfoFieldsPanel } from "./Panels/LiabilitiesInfoFieldsPanel";
import { NoFoundPage } from "../../../../pages/Anonymous/NoFoundPage";
import { ReimbursementsInfoFieldsPanel } from "./Panels/ReimbursementsInfoFieldsPanel";
import { SimplePayInvoiceInfoFieldsPanel } from "./Panels/SimplePayInvoiceInfoFieldsPanel";
import { SpecialDeductionsInfoFieldsPanel } from "./Panels/SpecialDeductionsInfoFieldsPanel";
import { SpecialEarningsInfoFieldsPanel } from "./Panels/SpecialEarningsInfoFieldsPanel";
import { TwoColumnLayout } from "./TwoColumnLayout";
import { setSelectedDrawerOptionsMenu } from "../../../../store/actions/drawerMenuOptionsActions";
import { setSelectedStickyNavBar } from "../../../../store/actions/stickyNavBarActions";
import { yupResolver } from "@hookform/resolvers/yup";
import { DepartmentsService } from "../../../../services/DepartmentsService";
import { AccountantService } from "../../../../services";
import { WSIBClassService } from "../../../../services/WSIBClassService";

export interface IAccountSettings { }
const SINGLE_RECORD_DATA_QUERY_KEY = "single-data-record";

const useGetData = (
	AccountSettingsPageName: string,
	clientId: string,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, AccountSettingsPageName, clientId, navigateToLogout],
		queryFn: (data) => {
			return AccountSettingsService.getAccountSettingsById(AccountSettingsPageName, clientId, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});

const getAccountSettingsDDL = (AccountSettingsPageName: string, clientId: string,) =>
	useQuery({
		queryKey: ["account-settings-ddl", AccountSettingsPageName, clientId],
		queryFn: (data) => {
			return AccountSettingsService.getAccountSettingsDDL(AccountSettingsPageName, clientId);
		},
		refetchOnWindowFocus: false,
	});

const DASHBOARD_API_DATA_QUERY_KEY = "dashboard-api"
const useGetDashboardMenuData = (
	clientId: string,
	taxYear: number
) =>
	useQuery({
		queryKey: [DASHBOARD_API_DATA_QUERY_KEY, clientId, taxYear],
		queryFn: (data) => {
			return DashboardServices.getDashboardMenuData(clientId, taxYear);
		},
		refetchOnWindowFocus: false,
	});


const useGetDepartmentDDLItems = (clientId: string) =>
	useQuery({
		queryKey: ['department-ddl', clientId],
		queryFn: (data) => {
			return DepartmentsService.getGetDepartmentsDDL(clientId);
		},
		refetchOnWindowFocus: false,
	});

const useGetAccountantDDL = () =>
	useQuery({
		queryKey: ["accountant-ddl"],
		queryFn: () => AccountantService.getGetAccountantDDL(),
		refetchOnWindowFocus: false,
	});


const ussClassDDL = (
) =>
	useQuery({
		queryKey: ["wsib-class-ddl"],
		queryFn: (data) => {

			return WSIBClassService.getGetWSIBClassDDL();
		},
		refetchOnWindowFocus: false,
	});
export const expenseInitialData = [
	{
		id: "1",
		acountId: null,
		class: null,
		department: null,
	},
];

export const AccountSettings: React.FC<IAccountSettings> = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const theme = useTheme();
	const subMenuVisibility = useSelector(
		(state: IAppState) => state.drawerAction.subMenuVisibility
	);
	const clientId = useSelector((state: IAppState) => state.client.id);
	const taxYear = useSelector((state: IAppState) => state.taxYear.year);
	const navigateToLogout = useNavigateToLogout();
	const [openDialog, setDialogOpen] = useState(false);
	const [openArchiveDialog, setArchiveDialogOpen] = useState(false);
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [singleArchive, setSingleArchive] = useState<boolean>(false);
	const [accountSettingsId, setAccountSettingsId] = useState<number | string>(0);
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const queryClient = useQueryClient();

	const [linkEmployeesUpdatedData, setLinkEmployeesUpdatedData] =
		useState(expenseInitialData);

	const { isPending: isPendingDashboardAPIData, data: dashboardApiData } = useGetDashboardMenuData(clientId, taxYear);
	const { isPending: isPendingDepartmentDDLItems, data: departmentDDLItems } = useGetDepartmentDDLItems(clientId);
	const { isPending: isAccountantDDLLoading, data: accountantDDL } = useGetAccountantDDL();
	const { isLoading: isClassDDLLoading, data: wsibClassSetupDDL } = ussClassDDL();

	// Fucking combine component props defines here
	const { id, AccountSettingsPageName } = useParams();

	// accountSettingsPageName would be like 
	// net_suite
	// zoho_books
	// xero
	// quick_books
	// kashoo

	const [accountSettingsPageName, setAccountSettingsPageName] = useState<string>("");
	useEffect(() => {
		if (AccountSettingsPageName && AccountSettingsPageName.length > 0) {

			setAccountSettingsPageName(AccountSettingsPageName);
			if (AccountSettingsPageName !== accountSettingsPageName) {
				formMethods.reset();
			}
		}
	}, [accountSettingsPageName, AccountSettingsPageName]);

	const [editRecordId, setEditRecordId] = useState(id);

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "name",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "name",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
	}, []);

	const onSnackbarClosed = (
		_event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const formMethods = useForm<any>({
		resolver: yupResolver(AccountSettingsValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",

	});

	const { isPending: isPendingAccountSettings, data: accountSettingsDDL } =
		getAccountSettingsDDL(accountSettingsPageName, clientId);

	console.log('accountSettingsDDL123456789', accountSettingsDDL)


	const formatLabel = (key: string) => {
		return key
			.replace(/([a-z])([A-Z])/g, "$1 $2")
			.replace(/accounts?$/i, " Accounts")
			.replace(/[_-]+/g, " ")
			.replace(/\s+/g, " ")
			.trim()
			.toLowerCase()
			.replace(/\b\w/g, (c) => c.toUpperCase());
	}
	const extractGroups = (source: Record<string, any>) => {
		const groups: any = [];

		Object.entries(source).forEach(([key, value]) => {
			if (Array.isArray(value)) {
				groups.push({
					label:
						key === "DIRECTCOSTS" ? "DIRECT COSTS" :
							key === "CURRLIAB" ? "CURRENT LIABILITY" :
								key === "TERMLIAB" ? "TERM LIABILITY" :
									formatLabel(key),
					slug: key,
					value: key,
					options: value,
				});
			} else if (value && typeof value === "object") {
				Object.entries(value).forEach(([nestedKey, nestedValue]) => {
					if (Array.isArray(nestedValue)) {
						groups.push({
							label: formatLabel(nestedKey),
							slug: nestedKey,
							value: nestedKey,
							options: nestedValue,
						});
					}
				});
			}
		});

		groups.unshift({
			label: "Multiple Wage Accounts",
			value: "multiple_wage_accounts",
		});
		return groups;
	}

	const updateAccountSettingsDDLWithParentChildDDL = useMemo(() => {
		if (accountSettingsDDL && accountSettingsDDL.allaccounts) return extractGroups(accountSettingsDDL.allaccounts);
	}, [accountSettingsDDL]);

	console.log('updateAccountSettingsDDLWithParentChildDDL', updateAccountSettingsDDLWithParentChildDDL)

	useEffect(() => {
		if (accountSettingsDDL && accountSettingsDDL.status === 400 && accountSettingsPageName === 'zoho_books') {
			navigate(`/add-ons/zoho-books/connect-zoho`)
		} else if (accountSettingsDDL && accountSettingsDDL.status === 400 && accountSettingsPageName === 'net_suite') {
			navigate(`/add-ons/net-suite/setup`)
		}
	}, [accountSettingsDDL])

	const { isPending: isPendingById, data: selectedDataRecord } = useGetData(
		accountSettingsPageName,
		clientId,
		// editRecordId,
		navigateToLogout
	);

	const { isPending: isSubmitting, mutate: submitAccountSettingsFormFields } =
		useMutation({
			mutationFn: AccountSettingsService.create,
			onSuccess: (res: IAccountSettings | IServerError | IResponse | any) => {

				if (res.status === 200) {
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					scrollPanelFieldsToTop();
					let path = `/add-ons/account-settings/${accountSettingsPageName}/${res.data.clientId}`;
					navigate(path);
					return Promise.all([

						queryClient.invalidateQueries({
							queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
						}),
					]);
				} else if (res.status === 201) {

					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					scrollPanelFieldsToTop();
					let path = `/add-ons/account-settings/${accountSettingsPageName}/${res.data.clientId}`;
					navigate(path);

					return Promise.all([
						queryClient.invalidateQueries({
							queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
						}),
					]);
				} else {
					setSnackbarData({
						open: true,
						message: res.message ? res.message : "Something went wrong!",
						severity: "error",
					});
				}
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});


	const onSubmit = (values: any) => {
		const payload = {
			accountSettingsPageName: accountSettingsPageName,
			addOnesType: accountSettingsPageName,
			clientId: clientId,
			id: id && id,
			navigateToLogout,
			expense: {
				grossAmount: values.grossAmount,
				overtime: values.overtime,
				vacationPay: values.vacationPay,
				commission: values.commission,
				nppBonus: values.nppBonus,
				holidayPay: values.holidayPay,
				cppCompany: values.cppCompany,
				eiCompany: values.eiCompany,
				qppCompany: values.qppCompany,
				qpipCompany: values.qpipCompany,
				qhsfCompany: values.qhsfCompany,
				csstCompany: values.csstCompany,
				wsib: values.wsib,
			},

			liabilities: {
				payrollLiabilityNetPay: values.payrollLiabilityNetPay,
				federalTax: values.federalTax,
				provincialTax: values.provincialTax,
				cppEmployee: values.cppEmployee,
				eiEmployee: values.eiEmployee,
				qppEmployee: values.qppEmployee,
				qpipEmployee: values.qpipEmployee,
				cppCompany: values.cppCompany,
				eiCompany: values.eiCompany,
				qppCompany: values.qppCompany,
				qpipCompany: values.qpipCompany,
				qhsfCompany: values.qhsfCompany,
				csstCompany: values.csstCompany,
				additionalTaxTd1: values.additionalTaxTd1,
				deuctionsAtSource: values.deuctionsAtSource,
				advanceDeductions: values.advanceDeductions,
				wsibPayable: values.wsibPayable,
			},

			specialEarnings: {
				abc: values.abc,
				earningsHours: values.earningsHours,
			},

			reimbursements: {
				reimbursement: values.reimbursement,
			},

			simplepayInvoice: {
				termsOrPaymentAccount: values.termsOrPaymentAccount,
				invoice: values.invoice,
			},

			specialDeductions: {
				deduction: values.deduction,
			},

			employeeAdvance: {
				termsOrPaymentAccount: values.termsOrPaymentAccount,
				advance: values.advance,
			},
		};

		console.log("payload", payload);
		submitAccountSettingsFormFields(payload);
	};

	useEffect(() => {
		if (clientId && selectedDataRecord) {

			Object.keys(selectedDataRecord).forEach((fieldName) => {
				const fieldValue = selectedDataRecord[fieldName];
				formMethods.setValue(fieldName, fieldValue ?? undefined);
				if (fieldName === "clientId") {
					let path = `/add-ons/account-settings/${accountSettingsPageName}/${clientId}`
					navigate(path)
				}
				if (fieldValue && typeof (fieldValue) === 'object') {
					Object.keys(fieldValue).forEach((objFieldName) => {
						const objFieldValue = fieldValue[objFieldName];
						formMethods.setValue(objFieldName, objFieldValue ?? undefined);
					});
				}
			});
		}
	}, [clientId, selectedDataRecord]);

	const { isPending: isDeleting, mutate: deleteAccountSettingsById } = useMutation({
		mutationFn: AccountSettingsService.delAccountSettings,
		onSuccess: (res: string | IServerError | IResponse | any) => {
			if (typeof res === "string") {

				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
			} else {
				setSnackbarData({
					open: true,
					message: res.message ? res.message : "Something went wrong.",
					severity: res.status === false ? "error" : res.success === false ? "error" : "success",
				});
			}
			formMethods.reset();
			scrollPanelFieldsToTop();
			let path = `/add-ons/account-settings/${accountSettingsPageName}`;
			navigate(path);
		},
		onError: (err) => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});


	const deleteAccountSettingsField = () => {
		if (singleDelete) {
			setSingleDelete(false);
			let delPayload = {
				accountSettingsPageName: accountSettingsPageName,
				clientId: clientId,
				navigateToLogout: navigateToLogout,
			}
			deleteAccountSettingsById(delPayload);
			setDialogOpen(false);
		}
	};

	const onDeleteClicked = () => {
		setDialogOpen(true);
		setSingleDelete(true);
		if (selectedDataRecord) {
			setAccountSettingsId(selectedDataRecord.id);
		}
	};

	const onCloseHandler = () => {
		setDialogOpen(false);
	};

	const expandedLabels = ["Add-Ons", `${getAddOnsScreenParentName(accountSettingsPageName)}`];
	const expandedAddOnsOrListItem = "Account Settings";
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (parentLabel === `${getAddOnsScreenParentName(accountSettingsPageName)}` && item.label === expandedAddOnsOrListItem) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: isExpanded,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: parentLabel === `${getAddOnsScreenParentName(accountSettingsPageName)}` && parentLabel === `${getAddOnsScreenParentName(accountSettingsPageName)}` && item.label === expandedAddOnsOrListItem && `/add-ons/account-settings/${AccountSettingsPageName}`
			};
			return enhancedItem;
		});
	};

	const addManageWageAccountToAccountSettings = (menu: any[], AccountSettingsPageName: string) => {
		const currentPageParentName = getAddOnsScreenParentName(AccountSettingsPageName);
		const manageWageAccount = {
			label: "Manage Wage Account",
			items: undefined,
			expanded: false,
			path: false,
			command: () => navigate(`/add-ons/manage-wage-account/${AccountSettingsPageName}`)
		};

		const addOns = menu.find(item => item.label === "Add-Ons");

		if (addOns && Array.isArray(addOns.items)) {
			// find "Xero" inside Add-Ons' items
			const currentPage = addOns.items.find((item: any) => item.label === currentPageParentName);

			if (currentPage) {
				if (!Array.isArray(currentPage.items)) {
					currentPage.items = [];
				}
				// append at the end
				currentPage.items.push(manageWageAccount);
			}
		}

		return menu;
	}

	useEffect(() => {
		// dispatch(
		// 	setSelectedActivityLogActions({
		// 		entityId1: id ? id : "",
		// 		entityId2: "",
		// 		entityId3: "",
		// 		screenName: CLIENT_SETUP_SCREEN_SLUG,
		// 		accountantName: clientName,
		// 		activityType:
		// 			"activity_type=create&activity_type=updated&activity_type=delete",
		// 		activityLogVisibility: true,
		// 	})
		// );
		dispatch(setSelectedStickyNavBar({ isSticky: true }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);

			if (selectedDataRecord && !selectedDataRecord.isMultipleWageAccount && AccountSettingsPageName) {
				addManageWageAccountToAccountSettings(enhancedData, AccountSettingsPageName);
			}
			console.log("enhancedData-=-=-=", enhancedData)
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions, selectedDataRecord, AccountSettingsPageName]);


	const onClickHereClickedHandler = async () => {
		try {
			const response = await AccountSettingsService.getLoginAccount(accountSettingsPageName, clientId);
			console.log("response123123", response)
			if (typeof (response) === "string") {
				// window.location.href = response;
				window.open(response, "_blank");
			}
		} catch { }
	}

	let acceptableAccountSettingPages = ["net_suite", "zoho_books", "xero", "quick_books", "kashoo"]

	return (
		<>{AccountSettingsPageName && acceptableAccountSettingPages.includes(AccountSettingsPageName) ? <Grid>
			<BackdropCircularProgress
				open={
					isSubmitting ||
					isPendingById ||
					isDeleting || isPendingAccountSettings ||
					isPendingDashboardAPIData || isAccountantDDLLoading || isPendingDepartmentDDLItems || isClassDDLLoading
				}
				testId="account-settings"
			/>
			{accountSettingsDDL && accountSettingsDDL.status === 400 && accountSettingsPageName === 'xero' ?
				<>
					<div
						style={{
							margin: "15px 0",
							fontSize: "1.5rem",
							fontWeight: "bold",
							fontFamily: "'Roboto','Helvetica','Arial',sans-serif",
						}}
					>
						{"Account Settings"}
					</div>
					<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
						<Grid size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }} sx={{ background: "#ffffff", padding: "2rem", alignItems: "center", textAlign: "center", fontSize: ".875rem", border: "1px solid rgba(0, 0, 0, 0.12)", borderRadius: "2px" }}>
							<Link
								to={`#`}
								onClick={onClickHereClickedHandler}
								className="no-underline"
								style={{ color: theme.palette.primary.light }}
							>
								Click here
							</Link>
							&nbsp;&nbsp;
							<span style={{ color: "#FF6600", fontSize: ".875rem" }}>
								to allow SimplePay to access your Organization(s).
							</span>
						</Grid>
					</Grid></>
				// : accountSettingsDDL && accountSettingsDDL.status === 400 && accountSettingsPageName === 'quick_books' ?
				// 	<>
				// 		<div
				// 			style={{
				// 				margin: "15px 0",
				// 				fontSize: "1.5rem",
				// 				fontWeight: "bold",
				// 				fontFamily: "'Roboto','Helvetica','Arial',sans-serif",
				// 			}}
				// 		>
				// 			{"Account Settings"}
				// 		</div>
				// 		<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
				// 			<Grid size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }} sx={{ background: "#ffffff" }}>
				// 				<StandardPanel
				// 					title="Integration Type"
				// 					testId={`special-earnings-info`}
				// 					variant="outlined"
				// 					borderBottom={false}
				// 					borderHide={true}
				// 				>
				// 					<>Coming Soon..</>
				// 				</StandardPanel>
				// 			</Grid>
				// 		</Grid></>
				: accountSettingsDDL && accountSettingsDDL.status === 400 && accountSettingsPageName === 'kashoo' ?
					<>
						<div
							style={{
								margin: "15px 0",
								fontSize: "1.5rem",
								fontWeight: "bold",
								fontFamily: "'Roboto','Helvetica','Arial',sans-serif",
							}}
						>
							{"Account Settings"}
						</div>
						<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
							<Grid size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }} sx={{ background: "#ffffff", padding: "2rem", alignItems: "center", textAlign: "center", fontSize: ".875rem", border: "1px solid rgba(0, 0, 0, 0.12)", borderRadius: "2px" }}>
								<Link
									to={`#`}
									// onClick={onClickHereClickedHandler}
									className="no-underline"
									style={{ color: theme.palette.primary.light }}
								>
									Connect With Kashoo
								</Link>
							</Grid>
						</Grid></>
					: accountSettingsDDL && accountSettingsDDL.expenseaccounts ?
						// :
						<Grid>

							<DeleteConfirmationDialog
								open={openDialog}
								deleteActionHandler={deleteAccountSettingsField}
								cancelActionHandler={onCloseHandler}
								testId="account-settings-confirmation-dialog"
							/>
							<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
								<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
									<FormProvider {...formMethods}>
										<form onSubmit={formMethods.handleSubmit(onSubmit)}>
											<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
												<Grid
													size={{ xs: 12, sm: 12, md: 8.5, lg: 8, xl: 8 }}
													sx={{
														// border: "1px solid rgba(0, 0, 0, 0.12)",
														// borderRadius: "3px",
														// marginTop: "16px",
														background: "#ffffff",
													}}
												>
													<TwoColumnLayout
														leftColumnContent={
															<>
																{accountSettingsPageName === "quick_books" ? <StandardPanel
																	title="Type Of Integration"
																	testId={`Type Of Integration-info`}
																	variant="outlined"
																	borderBottom={true}
																	borderHide={true}
																	showTitle={false}
																>
																	<EnhancedDropDownRHFWithLabel
																		selectName={"typeOfIntegration"}
																		items={[
																			{ label: "Online", value: 200 },
																			{ label: "Desktop", value: 100 },
																		]}
																		// selectedItem={"all"}
																		label={"Type Of Integration"}
																		testId={"type-of-integration-ddl"}
																		showEmpty
																	/>
																</StandardPanel> : <></>}

																<StandardPanel
																	title="Expense"
																	testId={`expense-info`}
																	variant="outlined"
																	borderBottom={true}
																	borderHide={true}
																>
																	<ExpenseInfoFieldsPanel
																		accountSettingsDDL={accountSettingsDDL && updateAccountSettingsDDLWithParentChildDDL}
																		// accountSettingsDDL={accountSettingsDDL && accountSettingsDDL?.expenseaccounts && sortDDLByLabel(accountSettingsDDL.expenseaccounts)}
																		formMethods={formMethods}
																		addedEmployeesprovince={dashboardApiData && dashboardApiData}

																		accountSettingsPageName={accountSettingsPageName}
																		accountDDL={accountantDDL}
																		classDDl={wsibClassSetupDDL}
																		departmentDDL={departmentDDLItems}
																		onLinkEmployeesDataChange={(data: any) =>
																			setLinkEmployeesUpdatedData(data)}
																		linkEmployeesUpdatedData={linkEmployeesUpdatedData}
																	/>
																</StandardPanel>
																<StandardPanel
																	title="Special Earnings"
																	testId={`special-earnings-info`}
																	variant="outlined"
																	borderBottom={true}
																	borderHide={true}
																>
																	<SpecialEarningsInfoFieldsPanel
																		testId={"special-earnings-info"}
																		accountSettingsDDL={accountSettingsDDL && updateAccountSettingsDDLWithParentChildDDL}
																	// accountSettingsDDL={accountSettingsDDL && accountSettingsDDL?.expenseaccounts && sortDDLByLabel(accountSettingsDDL.expenseaccounts)}
																	/>
																</StandardPanel>
																<StandardPanel
																	title="Reimbursements"
																	testId={`reimbursement-info`}
																	// borderBottom={true}
																	borderHide={true}
																	variant="outlined">

																	<ReimbursementsInfoFieldsPanel
																		testId={"reimbursement"}
																		accountSettingsDDL={accountSettingsDDL && updateAccountSettingsDDLWithParentChildDDL}
																	// accountSettingsDDL={accountSettingsDDL && accountSettingsDDL?.expenseaccounts && sortDDLByLabel(accountSettingsDDL.expenseaccounts)}
																	/>
																</StandardPanel>
																<StandardPanel
																	title="SimplePay Invoice"
																	testId={`simplePay-invoice-info`}
																	// borderBottom={true}
																	borderTop={true}
																	borderHide={true}
																	variant="outlined"
																>
																	<SimplePayInvoiceInfoFieldsPanel
																		testId={"simplePay-info"}
																		accountSettingsDDL={accountSettingsDDL && updateAccountSettingsDDLWithParentChildDDL}
																	// accountSettingsDDL={accountSettingsDDL && accountSettingsDDL?.expenseaccounts && sortDDLByLabel(accountSettingsDDL.expenseaccounts)}
																	/>
																</StandardPanel>
															</>
														}
														rightColumnContent={
															<>
																<StandardPanel
																	title="Liabilities"
																	testId={`liabilities-info`}
																	variant="outlined"
																	borderBottom={true}
																	borderHide={true}
																>
																	<LiabilitiesInfoFieldsPanel
																		testId={"liabilities"}
																		accountSettingsDDL={accountSettingsDDL && updateAccountSettingsDDLWithParentChildDDL}
																		addedEmployeesprovince={dashboardApiData && dashboardApiData}
																	// accountSettingsDDL={accountSettingsDDL && accountSettingsDDL?.liabilityaccounts && sortDDLByLabel(accountSettingsDDL.liabilityaccounts)}
																	/>
																</StandardPanel>
																<StandardPanel
																	title="Special Deductions"
																	testId={`special-deductions-info`}
																	borderBottom={true}
																	borderHide={true}
																	variant="outlined"
																>
																	<SpecialDeductionsInfoFieldsPanel
																		testId={"special-deductions-info"}
																		accountSettingsDDL={accountSettingsDDL && updateAccountSettingsDDLWithParentChildDDL}
																	// accountSettingsDDL={accountSettingsDDL && accountSettingsDDL?.liabilityaccounts && sortDDLByLabel(accountSettingsDDL.liabilityaccounts)}
																	/>
																</StandardPanel>
																<StandardPanel
																	title="Employee Advance"
																	testId={`employee-advance`}
																	borderHide={true}
																	variant="outlined"
																>
																	<EmployeeAdvanceInfoFieldsPanel
																		testId={"employee-advance"}
																		accountSettingsDDL={accountSettingsDDL && updateAccountSettingsDDLWithParentChildDDL}
																	// accountSettingsDDL={accountSettingsDDL && accountSettingsDDL?.liabilityaccounts && sortDDLByLabel(accountSettingsDDL.liabilityaccounts)}
																	/>
																</StandardPanel>
															</>
														}
														toolbar={
															<BasicSetupFormToolbar
																onDeleteClicked={onDeleteClicked}
																isSubmitting={isSubmitting}
																id={id} pageTitle={"Account Settings"}
															/>
														}
														testId="form-layout"
														showBorder={true}
													/>
												</Grid>
												<Grid size={{ xs: 12, sm: 12, md: 0.2, lg: 1, xl: 1 }}></Grid>
											</Grid>
										</form>
									</FormProvider>

								</Grid>
							</Grid>
							<EnhancedSnackbar
								message={snackbarData.message}
								onCloseHandler={onSnackbarClosed}
								severity={snackbarData.severity}
								testId={"account-settings-snackbar"}
								open={snackbarData.open}
							/>
						</Grid>
						: <></>
			}
		</Grid> : <NoFoundPage />}</>
	);
};
